import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { PermissionService } from '@core/services/permission.service';
import { LookupStore } from '@core/lookup/lookup.store';
import { LOOKUP_CODES } from '@core/lookup/lookup-codes';
import { Permission } from '@shared/enums/permission.enum';
import { InterventionStore } from '../../../intervention.store';
import { SERVICE_CLOSURE_OUTCOME_CODES } from '../../../intervention-closure';
import {
  InterventionReportResponse,
  InterventionReportType,
  SubmitInterventionReportRequest,
} from '../../../intervention.model';
import { nullIfBlank } from '@shared/utils/payload-utils';

type GoalRow = FormGroup<{ goalId: FormControl<string>; goal: FormControl<string>; outcome: FormControl<string> }>;

// Reports for the intervention: a provider (or staff) submits a close-out, outcome or session report, and internal
// staff approve it or return it for correction. The close-out report is the Case Closure Form. The server decides who
// may do what; this panel hides what the caller can't use.
@Component({
  selector: 'intervention-reports-panel',
  imports: [DatePipe, ReactiveFormsModule, ...SharedMaterialUIModule],
  templateUrl: './intervention-reports-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class InterventionReportsPanelComponent {
  protected readonly interventionStore = inject(InterventionStore);
  private readonly lookupStore = inject(LookupStore);
  private readonly permissionService = inject(PermissionService);
  private readonly formBuilder = inject(FormBuilder);

  readonly interventionId = input.required<string>();
  readonly interventionStatus = input<string | null>(null);

  protected readonly types: { type: InterventionReportType; label: string }[] = [
    { type: 'CloseOut', label: 'Close-out report' },
    { type: 'Outcome', label: 'Outcome report' },
    { type: 'Session', label: 'Session report' },
  ];

  protected readonly intervention = computed(() => this.interventionStore.selected());

  // The report that ends service delivery depends on the type (close-out for Self/Informal/Formal, outcome for
  // SBRA/CISD/Onsite); the other one is not offered. A session report is always available.
  private readonly closingReportType = computed(
    () => this.interventionStore.interventionTypes().find((item) => item.id === this.intervention()?.interventionTypeId)?.closingReportType ?? null,
  );
  protected readonly reportTypes = computed(() =>
    this.types.filter((item) => item.type === 'Session' || !this.closingReportType() || item.type === this.closingReportType()),
  );

  protected readonly formOpen = signal(false);
  protected readonly reviewingId = signal<string | null>(null);

  protected readonly form = this.formBuilder.nonNullable.group({
    reportType: ['CloseOut' as InterventionReportType],
    sessionId: [''],
    closureOutcomeId: [''],
    summary: [''],
    goalsAchieved: [''],
    recommendations: [''],
    functioningAtClosure: [''],
  });
  // The treatment goals the case manager set at referral (MW-161); the close-out records what was achieved against each.
  protected readonly goalOutcomes = new FormArray<GoalRow>([]);
  protected readonly reviewNotes = this.formBuilder.nonNullable.control('');

  protected readonly caseStatuses = computed(() =>
    this.lookupStore
      .items(LOOKUP_CODES.closureOutcome)
      .filter((item) => SERVICE_CLOSURE_OUTCOME_CODES.includes(item.code ?? '')),
  );

  // Sessions that were held or are still planned, for the session report and the form's "dates of service".
  protected readonly sessions = computed(() =>
    this.interventionStore.sessions().filter((session) => session.status !== 'Cancelled'),
  );

  private readonly serviceReached = computed(() => {
    const stages = this.interventionStore.lifecycleStages();
    const current = stages.indexOf(this.interventionStatus() ?? '');
    return current >= 0 && current >= stages.indexOf('ServiceDelivery');
  });

  // Submitting is service-delivery work (the permission sessions and attendance use); reviewing is internal.
  protected readonly canSubmit = computed(
    () =>
      this.permissionService.hasPermission(Permission.SessionsRecord) &&
      this.interventionStatus() !== 'Closed' &&
      this.serviceReached(),
  );

  protected readonly canReview = computed(
    () => this.permissionService.hasPermission(Permission.InterventionsUpdate) && this.interventionStatus() !== 'Closed',
  );

  // The dates sessions were attended, for the close-out form's "dates of service".
  protected readonly datesOfService = computed(() =>
    this.interventionStore
      .sessions()
      .filter((session) => session.status === 'Attended')
      .map((session) => session.sessionDate),
  );

  constructor() {
    this.lookupStore.load([LOOKUP_CODES.closureOutcome]);
    effect(() => {
      this.interventionStore.loadReports(this.interventionId());
      this.interventionStore.loadSessions(this.interventionId());
      this.interventionStore.loadReferralSummary(this.interventionId());
    });
  }

  protected selectedType(): InterventionReportType {
    return this.form.controls.reportType.value;
  }

  protected label(type: InterventionReportType): string {
    return this.types.find((item) => item.type === type)?.label ?? type;
  }

  protected open(): void {
    this.form.reset({
      reportType: this.closingReportType() ?? 'CloseOut',
      sessionId: '',
      closureOutcomeId: '',
      summary: '',
      goalsAchieved: '',
      recommendations: '',
      functioningAtClosure: '',
    });
    this.goalOutcomes.clear();
    for (const goal of this.interventionStore.referralSummary().treatmentGoals) {
      this.goalOutcomes.push(
        this.formBuilder.nonNullable.group({
          goalId: [goal.id],
          goal: [goal.goal],
          outcome: [goal.achievedOutcome ?? ''],
        }),
      );
    }
    this.reviewingId.set(null);
    this.formOpen.set(true);
  }

  protected closeForm(): void {
    this.formOpen.set(false);
  }

  // Close-out needs a case status; session needs a session and a summary; outcome needs a summary.
  protected canSave(): boolean {
    const value = this.form.getRawValue();
    switch (value.reportType) {
      case 'CloseOut':
        return !!value.closureOutcomeId;
      case 'Session':
        return !!value.sessionId && !!value.summary.trim();
      default:
        return !!value.summary.trim();
    }
  }

  protected save(): void {
    if (!this.canSave()) {
      return;
    }

    const value = this.form.getRawValue();
    const isSession = value.reportType === 'Session';
    const request: SubmitInterventionReportRequest = {
      reportType: value.reportType,
      sessionId: isSession ? value.sessionId : null,
      closureOutcomeId: value.reportType === 'CloseOut' ? value.closureOutcomeId : null,
      summary: nullIfBlank(value.summary),
      goalsAchieved: isSession ? null : nullIfBlank(value.goalsAchieved),
      recommendations: isSession ? null : nullIfBlank(value.recommendations),
      functioningAtClosure: isSession ? null : nullIfBlank(value.functioningAtClosure),
      goalOutcomes:
        value.reportType === 'CloseOut'
          ? this.goalOutcomes.getRawValue().map((row) => ({ goalId: row.goalId, outcome: nullIfBlank(row.outcome) }))
          : undefined,
    };

    this.interventionStore.submitReport(this.interventionId(), request, () => this.closeForm());
  }

  protected openReview(report: InterventionReportResponse): void {
    this.reviewNotes.reset('');
    this.formOpen.set(false);
    this.reviewingId.set(report.id);
  }

  protected closeReview(): void {
    this.reviewingId.set(null);
  }

  protected approve(report: InterventionReportResponse): void {
    this.interventionStore.reviewReport(this.interventionId(), report.id, 'approve', nullIfBlank(this.reviewNotes.value), () =>
      this.closeReview(),
    );
  }

  protected returnReport(report: InterventionReportResponse): void {
    const notes = this.reviewNotes.value.trim();
    if (!notes) {
      this.reviewNotes.markAsTouched();
      return;
    }

    this.interventionStore.reviewReport(this.interventionId(), report.id, 'return', notes, () => this.closeReview());
  }
}
